/**
 * index.html から読み込む jQuery + DataTables の最小型（@types/jquery 非依存）。
 * サンプル管理など DataTables 連携ページでのみ使用する。
 */

/** DataTables の言語ブロック（DataTables `language` オプション互換の一部） */
export type DataTablesLangBlock = {
  emptyTable: string;
  zeroRecords: string;
  info: string;
  infoEmpty: string;
  infoFiltered: string;
  /** 空文字推奨。プレースホルダ／aria で十分（「検索:」二重表示を避ける） */
  search: string;
  searchPlaceholder?: string;
  lengthMenu: string;
  paginate: {
    first: string;
    last: string;
    next: string;
    previous: string;
  };
};

export type DataTablesLanguagesMap = Record<string, DataTablesLangBlock>;

type DataTablesTranslateFn = (key: string, options?: { defaultValue?: string }) => string;

/**
 * i18n の現在言語で DataTables `language` オプションを組み立てる。
 * 言語ごとの同一ブロック複製は不要（`t()` が既に解決済み）。
 */
export function buildDataTablesLanguage(t: DataTablesTranslateFn): DataTablesLangBlock {
  return {
    emptyTable: t('datatables.emptyTable'),
    zeroRecords: t('datatables.zeroRecords'),
    info: t('datatables.info'),
    infoEmpty: t('datatables.infoEmpty'),
    infoFiltered: t('datatables.infoFiltered'),
    search: '',
    searchPlaceholder: t('datatables.search_placeholder', {
      defaultValue: t('datatables.search', { defaultValue: 'Search' }).replace(/[:：]\s*$/, ''),
    }),
    lengthMenu: t('datatables.lengthMenu'),
    paginate: {
      first: t('datatables.paginate.first'),
      last: t('datatables.paginate.last'),
      next: t('datatables.paginate.next'),
      previous: t('datatables.paginate.previous'),
    },
  };
}

export type DataTablesExportButtonsOptions = {
  t: DataTablesTranslateFn;
  /** colvis 対象。既定 `:not(.noVis)` */
  colvisColumns?: string | number[];
  /** 文字列または出力時点で評価する関数（日付付き名向き） */
  filename?: string | (() => string);
  excelTitle?: string;
  includePrint?: boolean;
  /** false で列表示ボタンを付けない（既定 true） */
  includeColvis?: boolean;
  /**
   * サーバー側 PDF など、Excel の直後・Print の直前に差し込むカスタムボタン。
   * （DataTables 標準 `extend: 'pdf'` ではないケース用）
   */
  extraPdfButton?: Record<string, unknown>;
  /**
   * true のとき CSV/Excel/PDF/印刷/列表示を 1 つのコレクションに畳む
   *（モバイル一覧の縦 chrome 削減向け）
   */
  asCollection?: boolean;
  /**
   * asCollection 時に列表示だけコレクション外へ出す（発見しやすくする）
   */
  colvisOutsideCollection?: boolean;
};

/** 標準の列表示切替 + CSV / Excel /（任意でカスタム PDF・印刷）ボタン群 */
function dtButtonLabel(iconClass: string, label: string): string {
  return `<i class="${iconClass}" aria-hidden="true"></i><span class="dt-btn-label">${label}</span>`;
}

function buildColvisButton(
  t: DataTablesTranslateFn,
  colvisColumns: string | number[]
): Record<string, unknown> {
  return {
    extend: 'colvis',
    text: dtButtonLabel('fas fa-columns', t('common.colvis')),
    className: 'btn btn-secondary btn-sm buttons-colvis',
    titleAttr: t('common.colvis_title', { defaultValue: t('common.colvis') }),
    columns: colvisColumns,
    showAll: true,
    postfixButtons: [
      {
        extend: 'colvisRestore',
        text: t('common.colvis_restore', { defaultValue: 'Restore visibility' }),
      },
    ],
  };
}

function buildExportButtonDefs(
  options: DataTablesExportButtonsOptions
): Record<string, unknown>[] {
  const {
    t,
    colvisColumns = ':not(.noVis)',
    filename,
    excelTitle,
    includePrint = true,
    includeColvis = true,
    extraPdfButton,
  } = options;

  const buttons: Record<string, unknown>[] = [];
  if (includeColvis) {
    buttons.push(buildColvisButton(t, colvisColumns));
  }

  buttons.push(
    {
      extend: 'csv',
      text: dtButtonLabel('fas fa-file-csv', t('common.csv')),
      className: 'buttons-csv',
      titleAttr: t('common.csv'),
      charset: 'utf-8',
      bom: true,
      ...(filename ? { filename } : {}),
      exportOptions: { columns: ':visible' },
    },
    {
      extend: 'excel',
      text: dtButtonLabel('fas fa-file-excel', t('common.excel')),
      className: 'buttons-excel',
      titleAttr: t('common.excel'),
      ...(excelTitle ? { title: excelTitle } : {}),
      ...(filename ? { filename } : {}),
      exportOptions: { columns: ':visible' },
    }
  );

  if (extraPdfButton) {
    const pdfLabel =
      typeof extraPdfButton.text === 'string' ? extraPdfButton.text : t('common.pdf');
    buttons.push({
      ...extraPdfButton,
      text: dtButtonLabel('fas fa-file-pdf', String(pdfLabel)),
      titleAttr: String(pdfLabel),
      className: [extraPdfButton.className, 'buttons-pdf'].filter(Boolean).join(' '),
    });
  }

  if (includePrint) {
    buttons.push({
      extend: 'print',
      text: dtButtonLabel('fas fa-print', t('common.print')),
      className: 'buttons-print',
      titleAttr: t('common.print'),
      exportOptions: { columns: ':visible' },
    });
  }

  return buttons;
}

export function buildDataTablesExportButtons(
  options: DataTablesExportButtonsOptions
): Record<string, unknown>[] {
  const includeColvis = options.includeColvis !== false;
  const colvisColumns = options.colvisColumns ?? ':not(.noVis)';

  if (options.asCollection && options.colvisOutsideCollection) {
    const exportOnly = buildExportButtonDefs({ ...options, includeColvis: false });
    return [
      ...(includeColvis ? [buildColvisButton(options.t, colvisColumns)] : []),
      {
        extend: 'collection',
        text: dtButtonLabel(
          'fas fa-download',
          options.t('sample.table_export_short', { defaultValue: '一覧出力' })
        ),
        className: 'btn btn-secondary btn-sm buttons-export-menu',
        titleAttr: options.t('sample.table_export_title', {
          defaultValue: '表示中の一覧をファイル出力',
        }),
        autoClose: true,
        buttons: exportOnly,
      },
    ];
  }

  const buttons = buildExportButtonDefs(options);
  if (!options.asCollection) return buttons;

  return [
    {
      extend: 'collection',
      text: dtButtonLabel(
        'fas fa-download',
        options.t('sample.table_export_short', { defaultValue: '一覧出力' })
      ),
      className: 'btn btn-secondary btn-sm buttons-export-menu',
      titleAttr: options.t('sample.table_export_title', {
        defaultValue: '表示中の一覧をファイル出力',
      }),
      autoClose: true,
      buttons,
    },
  ];
}

/** DataTables API（本アプリで呼ぶメソッドのみ） */
export interface DataTablesApi {
  table?: () => { node: () => HTMLElement | null };
  clear(): DataTablesApi;
  rows: { add(rows: unknown[]): DataTablesApi };
  draw(fullReset?: boolean): void;
  columns: { adjust(): void };
  destroy(remove?: boolean): void;
  row(node: unknown): { data(): unknown };
}

export interface KensukeJQueryChain {
  (sel: string): KensukeJQueryChain;
  closest(sel: string): KensukeJQueryChain & { length: number };
  css(styles: Record<string, string>): KensukeJQueryChain;
  find(sel: string): KensukeJQueryChain & { length: number };
  remove(): KensukeJQueryChain;
  removeClass(className: string): KensukeJQueryChain;
  data(key: string): unknown;
  is(sel: string): boolean;
  prop(name: string, value?: unknown): unknown;
  each(fn: (this: HTMLElement, index: number, element: HTMLElement) => void): KensukeJQueryChain;
  off(...args: unknown[]): KensukeJQueryChain;
  on(events: string, handler: (...args: unknown[]) => void): KensukeJQueryChain;
  on(events: string, selector: string, handler: (e: JQuery.Event) => void): KensukeJQueryChain;
  DataTable(opts?: Record<string, unknown>): DataTablesApi;
  length: number;
}

export interface KensukeJQuery {
  (sel: HTMLElement | string | null | undefined): KensukeJQueryChain;
  fn: {
    DataTable?: {
      isDataTable(el: HTMLElement | null): boolean;
    };
  };
}

let dataTablesErrModeConfigured = false;

/**
 * DataTables の tn/*（再初期化など）の警告ダイアログ・コンソール出力を抑止する。
 * 根本対処は各所での destroy／destroyDataTableIfPresent と併用すること。
 */
export function configureDataTablesErrorMode(): void {
  if (dataTablesErrModeConfigured || typeof window === 'undefined') return;
  const jq = (window as unknown as { jQuery?: { fn?: { dataTable?: { ext?: { errMode?: string } } } } })
    .jQuery;
  const ext = jq?.fn?.dataTable?.ext;
  if (ext) {
    ext.errMode = 'none';
    dataTablesErrModeConfigured = true;
  }
}

export function getKensukeJQuery(): KensukeJQuery | null {
  if (typeof window === 'undefined') return null;
  configureDataTablesErrorMode();
  const w = window as unknown as { jQuery?: KensukeJQuery };
  return w.jQuery ?? null;
}

/** 1ページしかないとき DataTables の「前へ / 1 / 次へ」を出さない */
export function shouldShowDataTablesPaginate(pageCount: number): boolean {
  return pageCount > 1;
}

type DataTablesPaginateApi = {
  page: { info: () => { pages: number } };
  table: () => { container: () => HTMLElement };
};

/**
 * 総ページ数が1以下なら paginate を隠す（フィルタ後の再描画でも drawCallback から呼ぶ）。
 */
export function syncDataTablesPaginateVisibility(api: DataTablesPaginateApi): void {
  const show = shouldShowDataTablesPaginate(api.page.info().pages);
  const el = api.table().container().querySelector<HTMLElement>('.dataTables_paginate');
  if (!el) return;
  el.hidden = !show;
  el.setAttribute('aria-hidden', show ? 'false' : 'true');
}
